import React, { useState, useCallback, useEffect } from 'react';
import i18next from 'i18next';
import { Repeat } from '../../StyledComponents/Repeat';
import Content from '../../StyledComponents/Content';
import { ProductPageContentLimit } from '../../StyledComponents/ProductPage';
import { H2 } from '../../StyledComponents/Heading';
import { ButtonInline } from '../../StyledComponents/Button';
import { TableColumn, TableColumnRow, TableColumnCell } from '../../StyledComponents/TableColumn';

import OpeningHours from './OpeningHours';
import PhoneNumber from '../../StyledComponents/PhoneNumber';
import Map from './Map';

import { Branch, Maybe } from '../../@types/codegen/types';
import BranchModal from './BranchModal';
import { formatPhonenumber } from '../../utils/phonenumbers';

interface BranchProps {
  branch?: Maybe<Branch>;
  loading: boolean;
}

const Branch = ({ branch, loading }: BranchProps) => {
  const [modal, setModal] = useState(false);

  const openModal = useCallback(() => setModal(true), []);
  const closeModal = useCallback(() => setModal(false), []);

  useEffect(() => {
    if (branch && modal) {
      setModal(false);
    }
  }, [branch]);

  if (!branch) {
    return null;
  }

  return (
    <>
      {modal && (
        <BranchModal loading={loading} connections={branch?.connections} onClose={closeModal} />
      )}
      <Repeat>
        <H2 noMargin>
          {branch?.location?.city
            ? i18next.t('item.thisCarIsAvailableAtOurBranch', { location: branch?.location?.city })
            : i18next.t('item.contact')}
        </H2>
      </Repeat>
      <Repeat>
        <Map position={branch?.location?.position} />
      </Repeat>
      {(branch?.connections?.length || 0) > 1 && (
        <Repeat>
          <Content>
            <p>
              {i18next.t('item.centralWarehouseCarDescriptionShort')}{' '}
              <ButtonInline
                as="span"
                role="button"
                tabIndex={0}
                onClick={openModal}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openModal();
                  }
                }}
                aria-label={i18next.t('item.clickHereToShowContactToAnotherBranch') || ''}
                inline
              >
                {i18next.t('item.clickHereToShowContactToAnotherBranch')}
              </ButtonInline>
              .
            </p>
          </Content>
        </Repeat>
      )}
      <Repeat>
        <ProductPageContentLimit>
          <Repeat>
            <TableColumn>
              {branch?.location?.streetAddress && branch?.location?.city && (
                <TableColumnRow>
                  <TableColumnCell>{i18next.t('common.address')}</TableColumnCell>
                  <TableColumnCell>
                    {`${branch?.location?.streetAddress}, ${branch?.location?.city}`}
                  </TableColumnCell>
                </TableColumnRow>
              )}
              {branch?.contact?.phonenumber && (
                <PhoneNumber phoneNumber={formatPhonenumber(branch.contact.phonenumber)} />
              )}
            </TableColumn>
          </Repeat>
          {branch?.openingHours && (
            <Repeat>
              <OpeningHours openingHours={branch.openingHours} />
            </Repeat>
          )}
        </ProductPageContentLimit>
      </Repeat>
    </>
  );
};

export default Branch;
